import React, { useState } from 'react'
import { X, Plus, Trash2, GripVertical } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { collectionsApi } from '../../services/collectionsApi'
import { validateDisplayTemplate } from '../../utils/collectionUtils'
import type { CollectionFieldType, CollectionField } from '../../types/collections'

interface CreateCollectionModalProps {
  onClose: () => void
  onSuccess: () => void
}

interface FieldWithName extends CollectionField {
  name: string
}

const FIELD_TYPES: { value: CollectionFieldType; label: string }[] = [
  { value: 'string', label: 'Text' },
  { value: 'int', label: 'Integer' },
  { value: 'decimal', label: 'Decimal' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'url', label: 'URL' },
  { value: 'email', label: 'Email' },
  { value: 'picture', label: 'Picture' },
]

interface SortableFieldItemProps {
  field: FieldWithName
  onRemove: (name: string) => void
}

const SortableFieldItem: React.FC<SortableFieldItemProps> = ({ field, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.name })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg"
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-move text-gray-400 hover:text-gray-600"
      >
        <GripVertical className="w-4 h-4" />
      </div>
      <div className="flex-1">
        <span className="font-medium">{field.name}</span>
        <span className="ml-2 text-sm text-gray-500">({field.type})</span>
        {field.required && (
          <span className="ml-2 text-xs bg-rose-100 text-rose-700 px-2 py-0.5 rounded">Required</span>
        )}
        {field.description && (
          <p className="text-sm text-gray-600 mt-1">{field.description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onRemove(field.name)}
        className="p-1 text-gray-600 hover:text-rose-600 transition-colors"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  )
}

const CreateCollectionModal: React.FC<CreateCollectionModalProps> = ({ onClose, onSuccess }) => {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [displayTemplate, setDisplayTemplate] = useState('')
  const [sortFields, setSortFields] = useState<string[]>([])
  const [fields, setFields] = useState<FieldWithName[]>([])
  const [newFieldName, setNewFieldName] = useState('')
  const [newFieldType, setNewFieldType] = useState<CollectionFieldType>('string')
  const [newFieldRequired, setNewFieldRequired] = useState(false)
  const [newFieldDescription, setNewFieldDescription] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setFields((items) => {
        const oldIndex = items.findIndex((item) => item.name === active.id)
        const newIndex = items.findIndex((item) => item.name === over.id)
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const handleAddField = () => {
    if (!newFieldName.trim()) {
      setError('Field name is required')
      return
    }

    if (fields.some(f => f.name === newFieldName)) {
      setError('Field name already exists')
      return
    }

    const newField: FieldWithName = {
      name: newFieldName,
      type: newFieldType,
      required: newFieldRequired,
      description: newFieldDescription || undefined,
      position: fields.length,
    }

    setFields([...fields, newField])

    // Reset field form
    setNewFieldName('')
    setNewFieldType('string')
    setNewFieldRequired(false)
    setNewFieldDescription('')
    setError(null)
  }

  const handleRemoveField = (fieldName: string) => {
    setFields(fields.filter(f => f.name !== fieldName))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!name.trim()) {
      setError('Collection name is required')
      return
    }

    if (fields.length === 0) {
      setError('At least one field is required')
      return
    }

    if (!displayTemplate.trim()) {
      setError('Display template is required')
      return
    }

    // Convert fields array back to object format with positions
    const fieldsObject: Record<string, CollectionField> = {}
    fields.forEach((field, index) => {
      const { name: fieldName, ...fieldConfig } = field
      fieldsObject[fieldName] = {
        ...fieldConfig,
        position: index,
      }
    })

    // Validate display template
    const validationError = validateDisplayTemplate(displayTemplate, fieldsObject)
    if (validationError) {
      setError(validationError)
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      await collectionsApi.create({
        name: name.trim(),
        description: description.trim() || undefined,
        config: {
          schema_version: 1,
          fields: fieldsObject,
          display_template: displayTemplate.trim(),
          sort_fields: sortFields.filter(f => f),
        },
      })
      onSuccess()
    } catch (err) {
      setError('Failed to create collection')
      console.error('Error creating collection:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">Create Collection</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {error && (
              <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Collection Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action"
                placeholder="e.g., People, Products, Tasks"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action"
                rows={3}
                placeholder="What is this collection for?"
              />
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Fields</h3>
              
              {fields.length > 0 && (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={fields.map(f => f.name)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2 mb-4">
                      {fields.map((field) => (
                        <SortableFieldItem
                          key={field.name}
                          field={field}
                          onRemove={handleRemoveField}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}

              <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Field Name
                    </label>
                    <input
                      type="text"
                      value={newFieldName}
                      onChange={(e) => setNewFieldName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action"
                      placeholder="e.g., name, email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type
                    </label>
                    <select
                      value={newFieldType}
                      onChange={(e) => setNewFieldType(e.target.value as CollectionFieldType)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action"
                    >
                      {FIELD_TYPES.map(({ value, label }) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (optional)
                  </label>
                  <input
                    type="text"
                    value={newFieldDescription}
                    onChange={(e) => setNewFieldDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action"
                    placeholder="Brief description of this field"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newFieldRequired}
                      onChange={(e) => setNewFieldRequired(e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Required field</span>
                  </label>
                  
                  <button
                    type="button"
                    onClick={handleAddField}
                    className="flex items-center gap-2 px-3 py-1.5 bg-secondary text-gray-700 rounded-lg hover:bg-secondary-dark transition-colors text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Add Field
                  </button>
                </div>
              </div>
            </div>

            {fields.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Template <span className="text-rose-500">*</span>
                </label>
                <p className="text-sm text-gray-500 mb-2">
                  Use field names in curly braces to define how items should be displayed.
                  Example: {'{first_name} {last_name}'}
                </p>
                <input
                  type="text"
                  value={displayTemplate}
                  onChange={(e) => setDisplayTemplate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action"
                  placeholder={`e.g., {${fields.map(f => f.name).join('} {')}} or custom format`}
                />
              </div>
            )}

            {fields.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sort Order
                </label>
                <p className="text-sm text-gray-500 mb-2">
                  Choose up to 2 fields to sort items by (first field is primary sort).
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Primary Sort</label>
                    <select
                      value={sortFields[0] || ''}
                      onChange={(e) => setSortFields([e.target.value, sortFields[1] || ''])}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action"
                    >
                      <option value="">No sorting</option>
                      {fields.map((field) => (
                        <option key={field.name} value={field.name}>
                          {field.name} ({field.type})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Secondary Sort</label>
                    <select
                      value={sortFields[1] || ''}
                      onChange={(e) => setSortFields([sortFields[0] || '', e.target.value])}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action"
                      disabled={!sortFields[0]}
                    >
                      <option value="">None</option>
                      {fields
                        .filter((field) => field.name !== sortFields[0])
                        .map((field) => (
                          <option key={field.name} value={field.name}>
                            {field.name} ({field.type})
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-action text-plum rounded-lg hover:bg-action-dark transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Creating...' : 'Create Collection'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateCollectionModal